import React, { useEffect, useMemo, useState } from "react";
import { api, extractError } from "../lib/api";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "./ui/dialog";
import { toast } from "sonner";
import { Loader2 } from "lucide-react";
import { isBackgroundJobResponse, backgroundJobQueuedToast } from "../lib/backgroundJobs";
import { tallyDirectSyncBlockedReason } from "../lib/tallyApproval";

function formatInr(n) {
  return `₹${Number(n || 0).toLocaleString("en-IN", { maximumFractionDigits: 2 })}`;
}

/**
 * On Sync: load pending mappings for one or more vouchers, let user fill exact Tally ledger names, then sync.
 *
 * Props:
 * - voucherId / voucherCode: single voucher
 * - voucherIds: bulk (optional array of { id, code })
 */
export default function TallySyncMappingDialog({
  open,
  onOpenChange,
  voucherId,
  voucherCode,
  voucherIds,
  onSynced,
}) {
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [payload, setPayload] = useState(null);
  const [drafts, setDrafts] = useState({}); // key -> { tally_ledger_name, tally_parent_group }

  const targets = useMemo(() => {
    if (Array.isArray(voucherIds) && voucherIds.length) {
      return voucherIds.map((row) => (
        typeof row === "number"
          ? { id: row, code: `#${row}` }
          : { id: row.id, code: row.code || `#${row.id}` }
      )).filter((row) => row.id);
    }
    if (voucherId) return [{ id: voucherId, code: voucherCode || `#${voucherId}` }];
    return [];
  }, [voucherId, voucherCode, voucherIds]);

  const targetKey = targets.map((t) => t.id).join(",");

  const [resolvedTargets, setResolvedTargets] = useState([]);

  useEffect(() => {
    if (!open || !targets.length) return undefined;
    let cancelled = false;
    setLoading(true);
    setPayload(null);
    setDrafts({});
    setResolvedTargets([]);

    // Expand to Material + Freight (TDS is already on those Tally booking vouchers).
    api
      .post("/tally/sync-family", { entity_kind: "voucher", entity_ids: targets.map((t) => t.id) })
      .then(({ data }) => {
        if (cancelled) return [];
        const family = (data?.vouchers || []).map((row) => ({
          id: row.id,
          code: row.code || `#${row.id}`,
          voucher_type: row.voucher_type,
          sync_approval_status: row.sync_approval_status,
        }));
        const expanded = (family.length ? family : targets).filter(
          (t) => String(t.voucher_type || "").toUpperCase() !== "TDS",
        );
        const blocked = expanded.map((t) => tallyDirectSyncBlockedReason(t)).find(Boolean);
        if (blocked) {
          toast.error(blocked);
          onOpenChange?.(false);
          return [];
        }
        setResolvedTargets(expanded);
        return Promise.all(
          expanded.map((t) =>
            api.get(`/tally/vouchers/${t.id}/sync-requirements`).then(({ data: req }) => ({ target: t, data: req })),
          ),
        );
      })
      .then((rows) => {
        if (cancelled || !rows) return;
        const errors = [];
        const missingByKey = new Map();
        const mappedByKey = new Map();
        const narrations = [];
        const eligibleTargets = [];
        let anyEligible = false;

        for (const { target, data } of rows) {
          if (!data?.eligible) {
            errors.push(`${target.code}: ${(data?.errors || []).join("; ") || "not eligible"}`);
            continue;
          }
          anyEligible = true;
          eligibleTargets.push(target);
          const typeLabel = target.voucher_type ? ` [${target.voucher_type}]` : "";
          if (data.narration) narrations.push(`${target.code}${typeLabel}: ${data.narration}`);
          for (const row of data.missing || []) {
            const prev = missingByKey.get(row.mapping_key);
            if (prev) {
              prev.amount = round2((prev.amount || 0) + (row.amount || 0));
              prev.voucher_codes = Array.from(new Set([...(prev.voucher_codes || []), target.code]));
            } else {
              missingByKey.set(row.mapping_key, {
                ...row,
                voucher_codes: [target.code],
              });
            }
          }
          for (const row of data.mapped || []) {
            if (!mappedByKey.has(row.mapping_key)) mappedByKey.set(row.mapping_key, row);
          }
        }

        const missing = Array.from(missingByKey.values()).sort((a, b) =>
          String(a.mapping_key).localeCompare(String(b.mapping_key)),
        );
        const mapped = Array.from(mappedByKey.values());
        setPayload({
          eligible: anyEligible,
          errors,
          narration: narrations.length === 1 ? narrations[0] : narrations.join("\n\n"),
          missing,
          mapped,
          titleCodes: (eligibleTargets.length ? eligibleTargets : rows.map((r) => r.target))
            .map((t) => t.code)
            .join(" · "),
          eligibleTargets,
          familyTypes: rows.map((r) => `${r.target.code} (${r.target.voucher_type || "?"})`).join(" · "),
        });
        const next = {};
        missing.forEach((row) => {
          next[row.mapping_key] = {
            tally_ledger_name: row.suggested_tally_name || "",
            tally_parent_group: row.default_parent_group || "",
          };
        });
        setDrafts(next);
      })
      .catch((err) => {
        if (!cancelled) {
          toast.error(extractError(err));
          onOpenChange?.(false);
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
    // targetKey captures voucher identity; targets codes update with same ids rarely.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, targetKey]);

  const missing = payload?.missing || [];
  const allFilled = useMemo(
    () => missing.every((row) => String(drafts[row.mapping_key]?.tally_ledger_name || "").trim()),
    [missing, drafts],
  );

  const updateDraft = (key, field, value) => {
    setDrafts((prev) => ({
      ...prev,
      [key]: { ...(prev[key] || {}), [field]: value },
    }));
  };

  const saveAndSync = async () => {
    const syncTargets = payload?.eligibleTargets?.length
      ? payload.eligibleTargets
      : (resolvedTargets.length ? resolvedTargets : targets);
    if (!syncTargets.length) return;
    if (missing.length && !allFilled) {
      toast.error("Fill every pending Tally ledger name");
      return;
    }
    setSaving(true);
    try {
      if (missing.length) {
        const mappings = missing.map((row) => ({
          mapping_key: row.mapping_key,
          tally_ledger_name: String(drafts[row.mapping_key]?.tally_ledger_name || "").trim(),
          tally_parent_group: String(drafts[row.mapping_key]?.tally_parent_group || "").trim() || null,
          is_active: true,
        }));
        await api.post("/tally/mappings/bulk", { mappings });
      }
      const { data } = await api.post("/tally/sync-bulk", {
        entity_kind: "voucher",
        entity_ids: syncTargets.map((t) => t.id),
      });
      if (isBackgroundJobResponse(data)) {
        const { title, message } = backgroundJobQueuedToast(data.job);
        toast.message(title, { description: message });
        onOpenChange?.(false);
        onSynced?.();
        return;
      }
      const n = (data?.jobs || []).length;
      toast.message(
        n
          ? `Queued ${n} Tally entr${n === 1 ? "y" : "ies"} — waiting for agent…`
          : "No Tally voucher to send (TDS is already on purchase/freight)",
      );
      if ((data?.errors || []).length) {
        toast.warning(`${data.errors.length} voucher(s) failed pre-flight`);
      }
      onOpenChange?.(false);
      onSynced?.();
    } catch (err) {
      toast.error(extractError(err));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-xl max-h-[90vh] overflow-y-auto sm:rounded-lg" data-testid="tally-sync-mapping-dialog">
        <DialogHeader>
          <DialogTitle>
            Sync to Tally · {payload?.titleCodes || voucherCode || (voucherId ? `#${voucherId}` : "vouchers")}
          </DialogTitle>
          <DialogDescription>
            Map any pending ledgers to exact Tally names, then push. For bank Payment/Receipt, map{" "}
            <strong>bank</strong> to your HDFC ledger. Purchase sync sends Material and Freight — TDS is already on those vouchers.
          </DialogDescription>
        </DialogHeader>

        {loading ? (
          <div className="flex items-center justify-center gap-2 py-10 text-sm text-muted-foreground">
            <Loader2 className="h-4 w-4 animate-spin" /> Checking mappings…
          </div>
        ) : !payload?.eligible ? (
          <p className="text-sm text-red-700 py-4">
            {(payload?.errors || []).join("; ") || "Voucher is not ready to sync."}
          </p>
        ) : (
          <div className="space-y-4">
            {payload?.familyTypes ? (
              <p className="text-xs text-muted-foreground rounded-md border border-border px-3 py-2">
                Entries: {payload.familyTypes}
              </p>
            ) : null}
            {(payload?.errors || []).length > 0 ? (
              <p className="text-xs text-amber-900 bg-amber-50 border border-amber-200 rounded-md px-3 py-2">
                Skipped: {payload.errors.join(" · ")}
              </p>
            ) : null}

            {payload?.narration ? (
              <div className="rounded-md border border-border bg-muted/30 px-3 py-2 text-xs">
                <p className="text-[10px] uppercase tracking-wider text-muted-foreground mb-1">Narration</p>
                <p className="text-foreground leading-relaxed whitespace-pre-wrap">{payload.narration}</p>
              </div>
            ) : null}

            {missing.length === 0 ? (
              <p className="text-sm text-emerald-800 bg-emerald-50 border border-emerald-200 rounded-md px-3 py-2">
                All ledgers for {targets.length === 1 ? "this voucher" : "these vouchers"} are mapped. Click Sync to push to Tally.
              </p>
            ) : (
              <div className="space-y-3">
                <p className="text-sm text-amber-900 bg-amber-50 border border-amber-200 rounded-md px-3 py-2">
                  {missing.length} field{missing.length === 1 ? "" : "s"} need mapping before sync.
                  Stock varieties map to Tally <strong>stock items</strong>; expenses/parties map to <strong>ledgers</strong>.
                </p>
                {missing.map((row) => {
                  const isStock = row.kind === "stock_item"
                    || String(row.mapping_key || "").startsWith("inventory:")
                    || row.mapping_key === "inventory";
                  return (
                  <div key={row.mapping_key} className="rounded-md border border-border p-3 space-y-2">
                    <div className="flex flex-wrap items-baseline justify-between gap-2">
                      <div className="min-w-0">
                        <p className="text-sm font-medium">{row.label}</p>
                        <p className="font-mono text-[11px] text-muted-foreground">{row.mapping_key}</p>
                        <p className="text-[11px] text-muted-foreground mt-0.5">
                          {isStock ? "Stock item" : "Ledger"}
                        </p>
                        {row.voucher_codes?.length > 1 ? (
                          <p className="text-[11px] text-muted-foreground mt-0.5">
                            Used by: {row.voucher_codes.join(", ")}
                          </p>
                        ) : null}
                      </div>
                      {row.amount ? (
                        <p className="text-xs tabular text-muted-foreground">{formatInr(row.amount)}</p>
                      ) : null}
                    </div>
                    <div className="space-y-1.5">
                      <Label htmlFor={`tally-name-${row.mapping_key}`}>
                        {isStock ? "Tally stock item name" : "Tally ledger name"}
                      </Label>
                      <Input
                        id={`tally-name-${row.mapping_key}`}
                        value={drafts[row.mapping_key]?.tally_ledger_name || ""}
                        onChange={(e) => updateDraft(row.mapping_key, "tally_ledger_name", e.target.value)}
                        placeholder={isStock ? "Exact stock item as in Tally" : "Exact ledger as in Tally"}
                        autoFocus={missing[0]?.mapping_key === row.mapping_key}
                      />
                    </div>
                    <div className="space-y-1.5">
                      <Label htmlFor={`tally-group-${row.mapping_key}`}>
                        {isStock ? "Stock group (optional)" : "Parent group (optional)"}
                      </Label>
                      <Input
                        id={`tally-group-${row.mapping_key}`}
                        value={drafts[row.mapping_key]?.tally_parent_group || ""}
                        onChange={(e) => updateDraft(row.mapping_key, "tally_parent_group", e.target.value)}
                        placeholder={
                          row.default_parent_group
                          || (isStock ? "e.g. Primary" : "e.g. Sundry Creditors")
                        }
                      />
                    </div>
                  </div>
                  );
                })}
              </div>
            )}

            {(payload?.mapped || []).length > 0 ? (
              <details className="text-xs text-muted-foreground">
                <summary className="cursor-pointer select-none">
                  Already mapped ({payload.mapped.length})
                </summary>
                <ul className="mt-2 space-y-1 pl-1">
                  {payload.mapped.map((row) => (
                    <li key={row.mapping_key} className="font-mono">
                      {row.mapping_key} → {row.suggested_tally_name}
                    </li>
                  ))}
                </ul>
              </details>
            ) : null}
          </div>
        )}

        <DialogFooter className="gap-2 sm:gap-0">
          <Button type="button" variant="outline" onClick={() => onOpenChange?.(false)} disabled={saving}>
            Cancel
          </Button>
          <Button
            type="button"
            onClick={saveAndSync}
            disabled={loading || saving || !payload?.eligible || (missing.length > 0 && !allFilled)}
            className="bg-bansal-sage hover:bg-bansal-sageDark text-white"
            data-testid="tally-sync-confirm"
          >
            {saving ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : null}
            {missing.length ? "Save mappings & Sync" : "Sync to Tally"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function round2(n) {
  return Math.round((Number(n) || 0) * 100) / 100;
}
